// Landing.js
import React, { useEffect, useState } from "react";
import { useData } from "contexts/DataContext";
import {
  Container,
  Box,
  Typography,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
} from "@mui/material";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import RocketIcon from "@mui/icons-material/Rocket";
import { Tooltip } from "@mui/material";

const Landing = () => {
  const eventID = "reciCY3fWqNor6eH4";
  const { events, teams, loading, error } = useData();
  const [filteredTeams, setFilteredTeams] = useState([]);

  const event = events.find((e) => e.id === eventID);

  // pull in the teams that belong to this event
  useEffect(() => {
    if (event && teams.length) {
      const eventTeams = event.data.teams
        .map((tid) => teams.find((t) => t.id === tid))
        .filter(Boolean);
      setFilteredTeams(eventTeams);
    }
  }, [event, teams]);

  if (loading) return <Typography>Loading event data…</Typography>;
  if (error) return <Typography color="error">{error}</Typography>;
  if (!event) return <Typography>Event not found</Typography>;

  // format date nicely
  const startDate = new Date(event.data["Event Start"]);
  const formattedDate = startDate.toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

  // helper to decide rocket type
  const rocketTypeFor = (team) =>
    team.data.team_category === "Senior Division"
      ? "Graduator Rocket"
      : "Green Eggs Rocket";

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Hero Section */}
      <Paper
        elevation={3}
        sx={{
          p: 4,
          mb: 6,
          textAlign: "center",
          backgroundImage: `url(${event.data["Event Image"]?.[0]?.url})`,
          backgroundSize: "cover",
          color: "#fff",
          position: "relative",
          "&:before": {
            content: '""',
            position: "absolute",
            inset: 0,
            bgcolor: "rgba(0,0,0,0.4)",
          },
        }}
      >
        <Box position="relative" zIndex={1}>
          <Typography variant="h2" component="h1" gutterBottom>
            {event.data["Event Name"]}
          </Typography>
          <Typography variant="h6" gutterBottom>
            {formattedDate} &middot; {event.data.Location}
          </Typography>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              flexDirection: { xs: "column", sm: "row" },
              gap: 2,
            }}
          >
            <Button
              variant="contained"
              size="large"
              href="/rockets/register"
              disabled={true}
              sx={{ mt: 2 }}
            >
              Registration Closed
            </Button>
            {/* External link to manuals & guides */}
            <Button
              variant="contained"
              color="secondary"
              size="large"
              href="https://www.most.org/learn/stem-challenges/"
              target="_blank"
              rel="noopener"
              sx={{ mt: 2 }}
            >
              Event Resources
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Teams Table */}
      <Typography variant="h5" gutterBottom>
        Registered Teams ({filteredTeams.length})
      </Typography>
      <Paper elevation={1}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: "primary.main" }}>
              {[
                "Team #",
                "Team Name",
                "Rocket Type",
                "Team Leader",
                "Members",
                "Reuse",
              ].map((label) => (
                <TableCell
                  key={label}
                  sx={{
                    fontWeight: "bold",
                    color: "common.white", // make text stand out on the darker bg
                  }}
                >
                  {label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredTeams.map((team) => {
              const td = team.data;
              // treat only a strict `true` as a kit request
              const requested = td.kit_requested === true;
              const reused = !requested;

              return (
                <TableRow
                  key={team.id}
                  sx={
                    td.withdrawn
                      ? {
                          "& .MuiTableCell-root": {
                            textDecoration: "line-through",
                            color: "text.disabled",
                          },
                        }
                      : {}
                  }
                >
                  <TableCell align="right">{td.team_num}</TableCell>
                  <TableCell>{td.team_name}</TableCell>
                  <TableCell>{rocketTypeFor(team)}</TableCell>
                  <TableCell>
                    {Array.isArray(td.team_leader_name)
                      ? td.team_leader_name[0]
                      : td.team_leader_name}
                  </TableCell>
                  <TableCell>{td.rollup_team_members_names}</TableCell>

                  {/* Reuse column */}
                  <TableCell align="center">
                    {reused ? (
                      <Tooltip title="Reusing own rocket → bonus sustainability point">
                        <AutorenewIcon color="success" />
                      </Tooltip>
                    ) : (
                      <Tooltip title="Requested a kit">
                        <RocketIcon />
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>
    </Container>
  );
};

export default Landing;
